import React, { useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Menu, X,
  Briefcase, TrendingUp, FileText, Building2, Users, Tag, CreditCard,
  Target, Award, Star, CalendarDays, ShoppingBag,
  ImagePlus, Shield,
  Download, Cpu, Languages, RotateCcw, CheckCircle2,
  RefreshCw, AlertTriangle, ExternalLink, ChevronDown
} from 'lucide-react';
import api, { adminAPI } from '@/utils/api';
import { openDocument, downloadDocument } from '@/utils/documents';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

// ─── Migration Modal (moved from AdminDashboard) ─────────────────────────────
function MigrationModal({ onClose }) {
  const [step, setStep] = useState('preview');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [rollbackResult, setRollbackResult] = useState(null);

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/admin/maintenance/jobs-migration-preview');
        setPreview(data);
      } catch {
        toast.error('Erro ao carregar preview.');
        onClose();
      } finally {
        setPreviewLoading(false);
      }
    })();
  }, [onClose]);

  const execute = async () => {
    setStep('running');
    try {
      const { data } = await api.post('/admin/maintenance/fix-jobs-pt');
      setResult(data);
      setStep('done');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao executar migração.');
      setStep('preview');
    }
  };

  const rollback = async () => {
    setStep('rolling_back');
    try {
      const { data } = await api.post('/admin/maintenance/rollback-jobs-pt');
      setRollbackResult(data);
      setStep('rolled_back');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao reverter.');
      setStep('done');
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="migration-modal">
      <Card className="max-w-lg w-full p-6 relative max-h-[90vh] overflow-y-auto bg-white">
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-gray-700"><X className="h-5 w-5" /></button>
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 rounded-lg bg-amber-100 text-amber-700"><Languages className="h-5 w-5" /></div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Migração de Títulos EN→PT</h3>
            <p className="text-xs text-gray-500">Corrige títulos em inglês nas vagas</p>
          </div>
        </div>

        {previewLoading && (
          <div className="text-center py-10 text-gray-500">
            <RefreshCw className="h-6 w-6 mx-auto mb-2 animate-spin opacity-40" />
            <p className="text-sm">A verificar vagas…</p>
          </div>
        )}

        {!previewLoading && step === 'preview' && preview && (
          <>
            {preview.total_affected === 0 ? (
              <div className="text-center py-6">
                <CheckCircle2 className="h-10 w-10 mx-auto mb-2 text-emerald-500" />
                <p className="font-semibold text-gray-700">Nenhuma vaga com títulos em inglês encontrada.</p>
                {preview.has_backup && (
                  <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
                    <p className="font-semibold mb-1">Backup disponível ({preview.backup_count} vaga(s)).</p>
                    <Button size="sm" variant="outline" className="border-amber-400 text-amber-700 mt-1" onClick={() => setStep('rollback_confirm')}>
                      <RotateCcw className="h-4 w-4 mr-1" /> Reverter migração anterior
                    </Button>
                  </div>
                )}
                <Button className="w-full mt-4" variant="outline" onClick={onClose}>Fechar</Button>
              </div>
            ) : (
              <>
                <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-100 text-sm text-blue-800">
                  <strong>{preview.total_affected} vaga(s)</strong> serão actualizadas.
                </div>
                <div className="space-y-2 mb-5">
                  <p className="text-xs font-semibold uppercase text-gray-400 tracking-wide mb-2">Alterações previstas:</p>
                  {preview.changes.map((ch, i) => (
                    <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-gray-50 border border-gray-100 text-sm">
                      <div className="flex-1 min-w-0">
                        <div className="text-red-600 font-medium truncate">«{ch.from}»</div>
                        <div className="text-xs text-gray-400 my-0.5">↓</div>
                        <div className="text-emerald-700 font-semibold truncate">«{ch.to}»</div>
                      </div>
                      <span className="shrink-0 text-xs text-gray-500 bg-white border border-gray-200 rounded-full px-2 py-0.5">{ch.count} vaga(s)</span>
                    </div>
                  ))}
                </div>
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800 mb-4">
                  <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />
                  Os títulos originais serão guardados para possível reversão.
                </div>
                <Button className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold" onClick={() => setStep('confirm')}>
                  Continuar para confirmação
                </Button>
              </>
            )}
          </>
        )}

        {step === 'confirm' && preview && (
          <>
            <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800 mb-5">
              <p className="font-bold mb-1">Confirmar execução?</p>
              <p>Irá actualizar <strong>{preview.total_affected} vaga(s)</strong>. Backup automático incluído.</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setStep('preview')}>Cancelar</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={execute}>Executar Migração</Button>
            </div>
          </>
        )}

        {step === 'running' && (
          <div className="text-center py-10">
            <RefreshCw className="h-8 w-8 mx-auto mb-3 animate-spin text-amber-500" />
            <p className="text-gray-600 font-medium">A executar migração…</p>
          </div>
        )}

        {step === 'done' && result && (
          <>
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-800 mb-4">
              <CheckCircle2 className="h-4 w-4 inline mr-1" />
              <strong>Migração concluída.</strong> {result.summary}
              {result.backup_saved > 0 && <div className="mt-1 text-xs">Backup de {result.backup_saved} título(s) guardado.</div>}
            </div>
            <Button variant="outline" className="w-full mb-2 border-red-200 text-red-700 hover:bg-red-50" onClick={() => setStep('rollback_confirm')}>
              <RotateCcw className="h-4 w-4 mr-2" /> Reverter para originais
            </Button>
            <Button className="w-full" onClick={onClose}>Fechar</Button>
          </>
        )}

        {step === 'rollback_confirm' && (
          <>
            <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800 mb-5">
              <p className="font-bold mb-1">Reverter migração?</p>
              <p>Os títulos serão restaurados para os valores originais do backup.</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setStep(result ? 'done' : 'preview')}>Cancelar</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={rollback}>Reverter Agora</Button>
            </div>
          </>
        )}

        {step === 'rolling_back' && (
          <div className="text-center py-10">
            <RefreshCw className="h-8 w-8 mx-auto mb-3 animate-spin text-red-400" />
            <p className="text-gray-600">A reverter…</p>
          </div>
        )}

        {step === 'rolled_back' && rollbackResult && (
          <>
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-800 mb-4">
              <CheckCircle2 className="h-4 w-4 inline mr-1" />
              <strong>Reversão concluída.</strong> {rollbackResult.summary}
            </div>
            <Button className="w-full" onClick={onClose}>Fechar</Button>
          </>
        )}
      </Card>
    </div>
  );
}

// ─── Nav Groups Data ─────────────────────────────────────────────────────────
const NAV_GROUPS = [
  {
    id: 'main',
    label: 'Principal',
    items: [
      { id: 'dashboard', label: 'Visão geral', path: '/admin', icon: LayoutDashboard },
      { id: 'prospects', label: 'Prospecção', path: '/admin/prospects', icon: Target, exclude: '/admin/prospects/agenda' },
      { id: 'agenda', label: 'Agenda de reuniões', path: '/admin/prospects/agenda', icon: CalendarDays, sub: true },
      { id: 'companies', label: 'Empresas', path: '/admin/companies', icon: Building2 },
      { id: 'jobs', label: 'Vagas', path: '/admin/jobs', icon: Briefcase, exclude: '/admin/jobs/overview' },
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing',
    items: [
      { id: 'instagram-kit', label: 'Kit Instagram & Logótipos', path: '/admin/instagram-kit', icon: ImagePlus },
      { id: 'trusted', label: 'Mural de Confiança', path: '/admin/trusted', icon: Shield },
    ],
  },
  {
    id: 'outros',
    label: 'Outros',
    collapsible: true,
    items: [
      { id: 'candidates', label: 'Candidatos', path: '/admin', icon: Users, neverActive: true },
      { id: 'categories', label: 'Categorias', path: '/admin/categories', icon: Tag },
      { id: 'purchases', label: 'Pagamentos', path: '/admin/purchases', icon: CreditCard },
      { id: 'jobs-overview', label: 'Desempenho de Vagas', path: '/admin/jobs/overview', icon: TrendingUp },
      { id: 'drafts', label: 'Vagas Internas', path: '/admin/drafts', icon: FileText },
      { id: 'ambassadors', label: 'Embaixadores', path: '/admin/ambassadors', icon: Award },
      { id: 'influencers', label: 'Influencers', path: '/admin/influencers', icon: Star },
      { id: 'events', label: 'Eventos e convites', path: '/admin/events', icon: CalendarDays },
      { id: 'commerce', label: 'Comercialização', path: '/admin/commerce', icon: ShoppingBag },
      { id: 'report-platform', label: 'Relatório Plataforma (PDF)', action: 'report-platform', icon: FileText },
      { id: 'report-candidates', label: 'Relatório Candidatos (PDF)', action: 'report-candidates', icon: FileText },
      { id: 'export-csv', label: 'Exportar CSV', action: 'export-csv', icon: Download },
      { id: 'interview-guide', label: 'Guião de entrevista (PDF)', action: 'interview-guide', icon: FileText },
      { id: 'ai', label: 'Modelos IA', path: '/admin/ai', icon: Cpu },
      { id: 'migration', label: 'Migração EN→PT', action: 'migration', icon: Languages },
    ],
  },
];

// ─── AdminSidebar Component ───────────────────────────────────────────────────
export default function AdminSidebar({ sidebarOpen, setSidebarOpen }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [loadingAction, setLoadingAction] = useState(null);
  const [showMigration, setShowMigration] = useState(false);
  const uploadRef = useRef(null);

  const isActive = (item) => {
    if (item.neverActive) return false;
    if (!item.path) return false;
    if (item.path === '/admin') return pathname === '/admin' || pathname === '/admin/dashboard';
    if (item.exclude && pathname.startsWith(item.exclude)) return false;
    return pathname === item.path || pathname.startsWith(item.path + '/');
  };
  const outrosGroup = NAV_GROUPS.find((g) => g.collapsible);
  const [outrosOpen, setOutrosOpen] = useState(() => outrosGroup.items.some((it) => !it.neverActive && it.path && it.path !== '/admin' && (pathname === it.path || pathname.startsWith(it.path + '/'))));

  const handleAction = async (action) => {
    if (action === 'howto-upload') {
      uploadRef.current?.click();
      if (sidebarOpen) setSidebarOpen(false);
      return;
    }
    if (action === 'migration') {
      setShowMigration(true);
      if (sidebarOpen) setSidebarOpen(false);
      return;
    }
    if (action === 'interview-guide') {
      navigate(`/documento?src=${encodeURIComponent('/admin/content-studio/interview-guide/pdf')}&name=trabadju-guiao-entrevista.pdf`);
      if (sidebarOpen) setSidebarOpen(false);
      return;
    }
    setLoadingAction(action);
    try {
      if (action === 'story') {
        toast.info('A gerar story Instagram…');
        const { data } = await api.post('/admin/story/generate', {}, { timeout: 120000 });
        await downloadDocument(data.url, data.filename || 'trabadju_story.png');
        toast.success('Story gerada com sucesso!');
      } else if (action === 'report-platform') {
        toast.info('A gerar relatório…');
        await downloadDocument('/admin/reports/platform-pdf', 'trabadju_relatorio_plataforma.pdf');
      } else if (action === 'report-candidates') {
        toast.info('A gerar relatório…');
        await downloadDocument(adminAPI.candidatesReportPdfUrl(), 'trabadju_candidatos_relatorio.pdf');
      } else if (action === 'export-csv') {
        toast.info('A exportar CSV…');
        await downloadDocument(adminAPI.candidatesReportCsvUrl(), 'trabadju_candidatos_agregado.csv');
      }
    } catch (e) {
      toast.error(e?.message || 'Erro ao executar a acção. Tente novamente.');
    } finally {
      setLoadingAction(null);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const formData = new FormData();
    formData.append('file', file);
    const uploadId = 'howto-upload';
    setLoadingAction(uploadId);
    try {
      toast.info('A carregar vídeo…');
      await api.post('/admin/howto-video/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      toast.success('Vídeo actualizado com sucesso!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao carregar o vídeo.');
    } finally {
      setLoadingAction(null);
    }
  };

  const NavItemEl = ({ item }) => {
    const active = isActive(item);
    const loading = loadingAction === item.id;
    const isActionItem = !item.path;

    const baseClasses = `
      flex items-center gap-2.5 w-full text-left px-3 py-2 rounded-lg text-sm font-medium
      transition-all duration-150 group
      ${active
        ? 'bg-[#3D63F5] text-white shadow-sm'
        : 'text-white/65 hover:text-white hover:bg-white/10'
      }
      ${isActionItem ? 'cursor-pointer' : ''}
    `;

    const iconClasses = `h-4 w-4 shrink-0 ${active ? 'text-white' : 'text-white/50 group-hover:text-white/80'} transition-colors`;
    const externalIconClasses = `h-3 w-3 shrink-0 ml-auto opacity-40 group-hover:opacity-70`;

    const content = (
      <>
        {loading
          ? <RefreshCw className={`${iconClasses} animate-spin`} />
          : <item.icon className={iconClasses} />
        }
        <span className="truncate">{item.label}</span>
        {isActionItem && !loading && <ExternalLink className={externalIconClasses} />}
      </>
    );

    if (isActionItem) {
      return (
        <button
          className={baseClasses}
          onClick={() => handleAction(item.action)}
          disabled={loading}
          data-testid={`sidebar-action-${item.id}`}
        >
          {content}
        </button>
      );
    }

    return (
      <button
        className={baseClasses}
        onClick={() => { navigate(item.path); if (sidebarOpen) setSidebarOpen(false); }}
        data-testid={`sidebar-nav-${item.id}`}
      >
        {content}
      </button>
    );
  };

  const SidebarInner = () => (
    <div className="flex flex-col h-full min-h-screen">
      {/* Header / Logo */}
      <div className="px-4 py-5 border-b border-white/10 flex items-center justify-between">
        <div>
          <div className="text-white font-bold text-xl tracking-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>
            trabadju
          </div>
          <div className="text-[10px] font-semibold tracking-[0.15em] uppercase text-[#F2B705] mt-0.5">
            Painel Admin
          </div>
        </div>
        {sidebarOpen && (
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-white/60 hover:text-white p-1">
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-5 overflow-y-auto">
        {NAV_GROUPS.map((group) => (
          <div key={group.id} className={group.collapsible ? 'border-t border-white/10 pt-4' : ''} data-testid={`sidebar-group-${group.id}`}>
            {group.collapsible ? (
              <button type="button" onClick={() => setOutrosOpen((o) => !o)} aria-expanded={outrosOpen}
                className="w-full flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.12em] text-white/45 hover:text-white/80 px-3 mb-1.5"
                data-testid="sidebar-outros-toggle">
                {group.label}
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${outrosOpen ? 'rotate-180' : ''}`} />
              </button>
            ) : group.label && (
              <p className={`text-[10px] font-semibold uppercase tracking-[0.12em] px-3 mb-1.5 ${group.id === 'main' ? 'text-[#F2B705]' : 'text-white/35'}`}>
                {group.label}
              </p>
            )}
            {(!group.collapsible || outrosOpen) && (
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <div key={item.id} className={item.sub ? 'pl-5' : ''}><NavItemEl item={item} /></div>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="px-4 py-4 border-t border-white/10">
        <p className="text-[11px] text-white/30 truncate">info@trabadju.cv</p>
        <p className="text-[10px] text-white/20">Administrador</p>
      </div>

      {/* Hidden file input for video upload */}
      <input
        ref={uploadRef}
        type="file"
        accept="video/*"
        className="hidden"
        onChange={handleVideoUpload}
      />
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex flex-col w-64 bg-[#0B1E44] sticky top-0 self-start h-screen shrink-0"
        data-testid="admin-sidebar"
      >
        <SidebarInner />
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="admin-sidebar-mobile">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setSidebarOpen(false)}
          />
          <aside className="absolute left-0 top-0 h-full w-72 bg-[#0B1E44] shadow-2xl overflow-y-auto">
            <SidebarInner />
          </aside>
        </div>
      )}

      {/* Migration Modal (global, rendered here) */}
      {showMigration && (
        <MigrationModal onClose={() => setShowMigration(false)} />
      )}
    </>
  );
}
